import { useLocalStorage } from "PosterEditor/hooks/use-locale-storage";
import { useState, useEffect, useCallback } from "react";
import { PosterProject, ProjectStore } from "PosterEditor/model/poster-project";
import { v4 as uuid } from "uuid";
import { defaultProject } from "PosterEditor/model/default-project-style";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { applyTemplateToProject } from "PosterEditor/model/project-template";
import { useAuthContext } from "BEDROCK/auth/auth-provider";

export const useProjectLocalStore = () => {
  const { isUserAuthenticated } = useAuthContext();
  const { save, read, remove } = useLocalStorage();
  const [projects, setProjects] = useState<PosterProject[]>([]);
  const [, forceUpdate] = useState(0);

  const createProject = useCallback(() => {
    const project = { ...defaultProject, id: uuid() };
    saveProject(project);
    return project;
  }, [isUserAuthenticated]);

  const createDraft = useCallback((template: ProjectTemplate | undefined) => {
    const project = {
      ...defaultProject,
      id: isUserAuthenticated ? uuid() : "guest_project",
      isTemplateDraft: true,
    };
    if (template) {
      applyTemplateToProject(template, project);
    }
    saveProject(project);
    return project;
  }, [isUserAuthenticated]);

  const draftProject = useCallback((
    createNew: boolean,
    template: ProjectTemplate | undefined
  ) => {
    if (createNew) {
      deleteDraft();
      return createDraft(template);
    }
    const draft = projects.find((p) => p.isTemplateDraft);
    if (!draft) {
      return createDraft(template);
    }
    return draft;
  }, [projects, createDraft]);

  const existingDraft = useCallback(() => {
    const draft = projects.find((p) => p.isTemplateDraft);
    return draft;
  }, [projects]);

  const deleteDraft = useCallback(() => {
    const projects = loadProjects() || [];
    const draftIndex = projects.findIndex((p) => p.isTemplateDraft);
    if (draftIndex === -1) return;
    const updatedProjects = projects.filter((p) => !p.isTemplateDraft);
    save("projectStore", updatedProjects);

    setProjects(updatedProjects);
  }, [save]);

  const deleteProject = useCallback((project: PosterProject) => {
    const prevProjects = loadProjects();
    const newProjects = prevProjects ? [...prevProjects] : [];
    const index = newProjects.findIndex((p) => p.id === project.id);
    let updatedProjects;
    if (index !== -1) {
      updatedProjects = newProjects.filter((p) => p.id !== project.id);
    } else {
      updatedProjects = [...newProjects];
    }
    save("projectStore", updatedProjects);
    setProjects(updatedProjects);
    return updatedProjects;
  }, [save]);

  const deleteAllProjects = useCallback(() => {
    setProjects([]);
    remove("projectStore");
  }, [remove]);

  const saveProject = useCallback((project: PosterProject) => {
    const projects = loadProjects();
    if (!projects) {
      setProjects([project]);
      save("projectStore", [project]);
      return;
    }
    const newProjects = projects;
    const index = projects.findIndex((p) => p.id === project.id);

    let updatedProjects;
    if (index !== -1) {
      updatedProjects = newProjects.map((p) =>
        p.id === project.id ? project : p
      );
    } else {
      updatedProjects = [{ ...project }, ...newProjects];
    }
    save("projectStore", updatedProjects);
    setProjects(updatedProjects);
    return updatedProjects;
  }, [save]);

  const loadProjects = useCallback((includeDrafts = true) => {
    const projects = read("projectStore") as PosterProject[] | undefined;
    if (!projects) {
      setProjects([]);
      return [];
    }

    const filteredProjects = includeDrafts
      ? projects
      : projects.filter((p) => !p.isTemplateDraft);
    setProjects(filteredProjects);
    return filteredProjects;
  }, [read]);

  const loadProject = useCallback((id: string) => {
    const projects = read("projectStore") as PosterProject[] | undefined;
    return projects?.filter((project) => project.id === id)[0];
  }, [read]);

  // Side-effect-free read from localStorage (does NOT call setProjects)
  const readProjectsLocal = useCallback((): PosterProject[] => {
    const projects = read("projectStore") as PosterProject[] | undefined;
    return projects || [];
  }, [read]);

  // Fully replaces localStorage contents and updates React state
  const replaceAllProjectsLocal = useCallback((projects: PosterProject[]) => {
    save("projectStore", projects);
    setProjects(projects);
  }, [save]);

  // Removes all projects except those in checkout/ordered + the excepted ID
  const removeAllNotInCheckoutLocal = useCallback((exceptID: string) => {
    const projects = readProjectsLocal();
    const filteredProjects = projects.filter(
      (p) => p.isInCheckout || p.isOrdered || p.id === exceptID
    );
    replaceAllProjectsLocal(filteredProjects);
  }, [readProjectsLocal, replaceAllProjectsLocal]);

  return {
    loadProjects,
    loadProject,
    saveProject,
    createProject,
    deleteProject,
    deleteAllProjects,
    draftProject,
    existingDraft,
    deleteDraft,
    readProjectsLocal,
    replaceAllProjectsLocal,
    removeAllNotInCheckoutLocal,
  };
};
